import axios from 'axios';
import { API_URL } from '../apiConfig';
import { browserHeaders, refreshBrowserSession, sessionTokenForRequest } from '../browserSession';

const instance = axios.create({
    baseURL: API_URL,
    withCredentials: true,
});

const isPublic = path => ['/auth/login', '/auth/register', '/auth/password-reset/request', '/auth/password-reset/confirm'].includes(path);
instance.interceptors.request.use((config) => {
    const token = sessionTokenForRequest('token');
    Object.assign(config.headers, browserHeaders(isPublic(config.url) ? null : token));
    return config;
});

let sessionCheck;
// Only our authentication response confirms an expired session. A provider or
// proxy can also return 401 while the application's login remains valid.
const isSessionRejected = error => error.response?.status === 401 && error.response?.data?.code === 'UNAUTHENTICATED';
function checkSession(token) {
    if (sessionCheck?.token === token) return sessionCheck.promise;
    const promise = instance.get('/api/account', { skipSessionExpiry: true, timeout: 10000 })
        .then(() => 'active', error => isSessionRejected(error) ? 'expired' : 'unknown')
        .finally(() => { if (sessionCheck?.promise === promise) sessionCheck = null; });
    sessionCheck = { token, promise };
    return promise;
}

instance.interceptors.response.use(response => response, async error => {
    if (error.response?.status === 409 && error.response?.data?.code === 'SESSION_CHANGED') refreshBrowserSession();
    const path = error.config?.url;
    const token = localStorage.getItem('token');
    if (error.response?.status === 401 && !isPublic(path) && !error.config?.skipSessionExpiry
            && token && error.config?.headers?.['X-Breck-Session'] === token) {
        const state = path === '/api/account' ? (isSessionRejected(error) ? 'expired' : 'unknown') : await checkSession(token);
        // An in-flight request for an old session must never clear a newer login.
        if (state === 'expired' && localStorage.getItem('token') === token) {
            window.dispatchEvent(new Event('apex:session-expired'));
        } else if (state === 'active') {
            error.response.data = { message: 'This request could not be completed. You are still signed in. Please try again shortly.' };
        }
    }
    return Promise.reject(error);
});

export default instance;
